/**
 * Canonical slug helpers for programmatic SEO URLs (used by the sitemap
 * generator, client-side route builders, and server-side SEO/redirect
 * middleware). Centralizing this logic prevents the class of bugs where
 * "-converter" gets appended more than once (e.g. "/3ds-to-converter-converter")
 * or a raw format value leaks stray punctuation into the URL (e.g. a leading
 * "." from a file extension producing "/.7z-to-...-converter").
 */

/**
 * Normalize a single format segment (e.g. "3DS", ".7z", "TAR.7Z") into a
 * clean, lowercase URL segment. Strips any leading/trailing dots (a raw file
 * extension like ".7z" should become "7z"), collapses internal dot/hyphen
 * runs, and removes any characters that aren't alphanumeric, dots, or
 * hyphens. Internal dots are preserved (e.g. "tar.7z" is a real format).
 */
export function normalizeFormatSegment(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^\.+|\.+$/g, "") // strip leading/trailing dots (e.g. ".7z" -> "7z")
    .replace(/[^a-z0-9.-]/g, "") // drop anything not alphanumeric/dot/hyphen
    .replace(/-{2,}/g, "-") // collapse double-hyphens
    .replace(/^-+|-+$/g, ""); // trim stray leading/trailing hyphens
}

/**
 * Strip any number of trailing "-converter" suffixes from a slug, so callers
 * can safely append exactly one without ever producing
 * "...-converter-converter".
 */
function stripConverterSuffix(slug: string): string {
  return slug.replace(/(?:-converter)+$/i, "");
}

/** Build a clean "/{format}-converter" slug (e.g. "/pdf-converter"). */
export function buildFormatConverterSlug(format: string): string {
  const clean = stripConverterSuffix(normalizeFormatSegment(format));
  return `${clean}-converter`;
}

/** Build a clean "/{category}-converter" slug (e.g. "/image-converter"). */
export function buildCategoryConverterSlug(category: string): string {
  const clean = stripConverterSuffix(
    normalizeFormatSegment(category.replace(/\s+/g, "-")),
  );
  return `${clean}-converter`;
}

/**
 * Build a clean "/{from}-to-{to}-converter" slug (e.g.
 * "/pdf-to-docx-converter"), guaranteeing the "converter" suffix is only
 * ever appended once even if the inputs already carry a stray suffix.
 */
export function buildFormatPairConverterSlug(
  fromFormat: string,
  toFormat: string,
): string {
  const from = stripConverterSuffix(normalizeFormatSegment(fromFormat));
  const to = stripConverterSuffix(normalizeFormatSegment(toFormat));

  // "converter" (or an empty string) is never itself a real file format —
  // if a segment reduces to this, the caller mis-split a hub URL like
  // "/3ds-converter" into fromFormat="3ds"/toFormat="converter" upstream.
  // Failing loudly here surfaces that bug instead of silently emitting
  // "3ds-to-converter-converter" or "3ds-to--converter".
  if (!from || !to || from === "converter" || to === "converter") {
    throw new Error(
      `buildFormatPairConverterSlug: invalid format pair ("${fromFormat}", "${toFormat}")`,
    );
  }

  return `${from}-to-${to}-converter`;
}

/**
 * Single source of truth for the URL shapes below, shared by the server
 * (seoInjection.ts, deciding whether to SSR/redirect a path) and the client
 * (DynamicConvertPage.tsx, deciding how to parse the same path). Previously
 * each side had its own hand-rolled copy of these regexes; letting them
 * drift apart is exactly what caused the server and client to disagree on
 * what a URL means (server redirects/SSRs one interpretation, client parses
 * another), which shows up as either an unexpected 404/redirect loop or a
 * client-side crash when the two disagree on fromFormat/toFormat.
 *
 * Segments only ever contain lowercase letters, digits, and dots (a real
 * format can contain a dot, e.g. "tar.7z") — never a bare hyphen, since
 * hyphens are the delimiters between segments themselves.
 */
export const FORMAT_PAIR_CONVERTER_PATTERN =
  /^\/?([a-z0-9.]+)-to-([a-z0-9.]+)-converter$/i;
export const FORMAT_CONVERTER_HUB_PATTERN = /^\/?([a-z0-9.]+)-converter$/i;
// Legacy two-segment pair URL, e.g. "/pdf-docx" — deliberately excludes
// anything already matching the two patterns above (callers should check
// those first) so it only ever matches genuinely legacy paths.
export const LEGACY_FORMAT_PAIR_PATTERN = /^\/?([a-z0-9.]+)-([a-z0-9.]+)$/i;

export interface ParsedFormatPair {
  fromFormat: string;
  toFormat: string;
}

/**
 * Parse a "/{from}-to-{to}-converter" path into its lowercase from/to
 * formats, or null if the path doesn't match that shape. Used by both the
 * server (to decide what to SSR) and the client (DynamicConvertPage's
 * synchronous fast path) so they can never disagree on the result for the
 * same URL.
 */
export function parseFormatPairConverterUrl(
  path: string,
): ParsedFormatPair | null {
  const match = path.match(FORMAT_PAIR_CONVERTER_PATTERN);
  if (!match) return null;
  return {
    fromFormat: match[1].toLowerCase(),
    toFormat: match[2].toLowerCase(),
  };
}
